import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, Subscription } from 'rxjs';
import { DropdownCoordinatorService } from '../../core/services/dropdown-coordinator/dropdown-coordinator.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslatePipe],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() isOpen: boolean = false;
  @Output() isOpenChange = new EventEmitter<boolean>();

  @Input() customerName: string = '';
  @Input() customerSubtitle: string = '';
  @Input() imageUrl: string = './../assets/defaultImage.webp';
  @Input() defaultImage: string = './../assets/defaultImage.webp';
  @Input() isJubooAgent: boolean = false;
  @Input() selectedCustomer: any = null;

  @Input() set customers(value: any[]) {
    this._customers.set(value || []);
  }

  @Output() customerSelected = new EventEmitter<any>();

  @ViewChild('clientCardDropdownRef') clientCardDropdownRef?: ElementRef;

  private _customers = signal<any[]>([]);
  searchTerm = signal<string>('');
  private dropdownCoordinator = inject(DropdownCoordinatorService);

  get showCustomerList(): boolean {
    return this.dropdownCoordinator.isOpen('sidebar_customer');
  }

  filteredCustomers = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    const allCustomers = this._customers();
    if (!term) {
      return allCustomers;
    }
    return allCustomers.filter((c) => c.name && c.name.toLowerCase().includes(term));
  });

  isCollapsed: boolean = false;
  currentUrl: string = '';

  private routerSub?: Subscription;

  constructor(private router: Router) {}

  ngOnInit(): void {
    this.currentUrl = this.router.url;
    this.routerSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentUrl = event.urlAfterRedirects || event.url;
        this.closeMobile();
      });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  get navQueryParams(): Record<string, any> {
    return this.isJubooAgent && this.selectedCustomer ? { customerId: this.selectedCustomer.id } : {};
  }

  isRouteActive(routes: string[]): boolean {
    const cleanUrl = this.currentUrl.split('?')[0];
    return routes.some((r) => cleanUrl.startsWith(r));
  }

  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    this.dropdownCoordinator.close('sidebar_customer');
    this.searchTerm.set('');
  }

  closeMobile(): void {
    if (this.isOpen) {
      this.isOpen = false;
      this.isOpenChange.emit(false);
    }
  }

  onClientCardClick(event?: Event): void {
    if (!this.isJubooAgent) {
      return;
    }
    if (this.showCustomerList) {
      this.dropdownCoordinator.close('sidebar_customer');
      this.searchTerm.set('');
    } else {
      this.dropdownCoordinator.open('sidebar_customer');
    }
  }

  onSearch(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm.set(input.value);
  }

  selectCustomer(customer: any): void {
    this.dropdownCoordinator.close('sidebar_customer');
    this.searchTerm.set('');
    this.customerSelected.emit(customer);
    this.closeMobile();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    if (
      this.showCustomerList &&
      this.clientCardDropdownRef &&
      !this.clientCardDropdownRef.nativeElement.contains(target)
    ) {
      this.dropdownCoordinator.close('sidebar_customer');
      this.searchTerm.set('');
    }
  }
}
